/**
 * Add someone to this site.
 *
 * The design's drawer is a name field, a checklist of what they will need, and
 * two actions: "Add & scan cards" or "Save without documents". Scanning is now
 * uploading, so the primary action adds the person and drops straight into
 * their profile with the first requirement's upload form open — which is what
 * "and scan cards" meant.
 *
 * The role picker is new. The mockup hardcodes Installer and says so in the
 * hint, but the required-document checklist below it is *derived from the
 * role*, so a drawer that always says "Installer" shows an electrician the
 * wrong four documents at the one moment they are being told what to bring.
 */
import { FileCheck2, Upload, UserPlus } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import type { CrewMember, CrewRole, CrewRoster } from "@gb/contracts";
import { Field } from "@/components/gb/field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
} from "@/components/ui/sheet";
import { docTypeLabel, CREW_ROLE_LABELS, CrewRoleSchema } from "@gb/contracts";
import { useAddCrewMember } from "./crew-api";

// Derived, not restated: the values are the contract's enum and the words are
// its label map, so a sixth role appears here the moment one is added.
const ROLES: { value: CrewRole; label: string }[] = CrewRoleSchema.options.map(
  (value) => ({
    value,
    label: CREW_ROLE_LABELS[value],
  }),
);

export function AddCrewDrawer({
  projectId,
  jurisdiction,
  open,
  onClose,
  onAdded,
}: {
  projectId: string;
  jurisdiction: CrewRoster["jurisdiction"];
  open: boolean;
  onClose: () => void;
  /** `andUpload` opens their profile ready to take the first document. */
  onAdded: (person: CrewMember, andUpload: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [role, setRole] = useState<CrewRole>("installer");
  const [error, setError] = useState<string | null>(null);
  const addMember = useAddCrewMember(projectId);

  useEffect(() => {
    if (open) {
      setName("");
      setRole("installer");
      setError(null);
    }
  }, [open]);

  const required = jurisdiction?.roleProfiles[role] ?? [];
  const labelFor = (type: string): string =>
    docTypeLabel(type, jurisdiction?.docLabels);

  const submit = (andUpload: boolean) => (event?: FormEvent) => {
    event?.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Who are you adding?");
      return;
    }
    addMember.mutate(
      { name: name.trim(), role },
      {
        onSuccess: (person) => onAdded(person, andUpload),
        onError: (caught: unknown) =>
          setError(
            caught instanceof Error ? caught.message : "Could not add them.",
          ),
      },
    );
  };

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent className="flex w-[480px] max-w-full flex-col">
        <SheetHeader>Add crew member</SheetHeader>

        <SheetBody>
          <form className="flex flex-col gap-4" onSubmit={submit(true)}>
            <Field label="Full name">
              {(props) => (
                <Input
                  {...props}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Tomáš Horváth"
                  autoFocus
                />
              )}
            </Field>

            <Field label="Role" hint="Sets which certifications they need.">
              {(props) => (
                <Select
                  value={role}
                  onValueChange={(v) => setRole(v as CrewRole)}
                >
                  <SelectTrigger id={props.id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            {error !== null && (
              <p
                role="alert"
                className="text-[0.84rem] font-semibold text-danger"
              >
                {error}
              </p>
            )}
          </form>

          <div className="mt-6 text-center">
            <div className="mx-auto mb-3.5 flex size-[60px] items-center justify-center rounded-tile border-[1.5px] border-border bg-brand-tint text-brand">
              <Upload size={24} aria-hidden />
            </div>
            <div className="font-display text-[1.1rem] font-bold uppercase tracking-[0.01em] text-ink">
              Onboard by uploading cards
            </div>
            <p className="mx-auto mt-2 max-w-[320px] text-[0.84rem] leading-[1.5] text-muted">
              Upload a photo or PDF of each certification — GroundBrain reads
              the issuing body and expiry, and you confirm them. Or save now and
              add documents later.
            </p>
          </div>

          {required.length > 0 ? (
            <div className="mt-[18px] flex flex-col gap-2.5">
              {required.map((type) => (
                <div
                  key={type}
                  className="flex items-center gap-[11px] rounded-card border-[1.5px] border-border bg-surface p-3.5"
                >
                  <span className="flex text-muted">
                    <FileCheck2 size={17} aria-hidden />
                  </span>
                  <span className="flex-1 text-[0.9rem] font-medium text-ink">
                    {labelFor(type)}
                  </span>
                  <span className="rounded-full bg-subtle px-2.5 py-1 text-[0.74rem] font-semibold text-muted">
                    Pending
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-[18px] text-center text-[0.84rem] text-muted">
              No compliance profile is configured for this project&rsquo;s
              country, so no documents are required yet.
            </p>
          )}
        </SheetBody>

        <SheetFooter>
          <Button
            size="lg"
            disabled={addMember.isPending}
            onClick={submit(true)}
          >
            <Upload size={16} />
            {addMember.isPending ? "Adding…" : "Add & upload cards"}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            disabled={addMember.isPending}
            onClick={submit(false)}
          >
            <UserPlus size={16} />
            Save without documents
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
